var _ = require('underscore');
var SecureMessageDataBehavior = require('./../secure-message/SecureMessageDataBehavior');
var TorsoView = require('backbone-torso/modules/View');
var billTabsTemplate = require('./billTabsTemplate.hbs');
var BillsDueListWidget = require('./BillsDueListWidget');
var BillsPaidListWidget = require('./PaidBillsListWidget');
var MakePaymentWidget = require('./../payment/MakePaymentWidget');
var ReceiptMessagesListWidget = require('./../receipt/ReceiptMessagesListWidget');
var EstimatesBrecListView = require('./../estimate/EstimatesBrecListView');
var EstimateDetailsWidget = require('./../estimate/EstimateDetailsWidget');
var BillDetailsView = require('./BillDetailsView');
var ReceiptDetailsWidget = require('./../receipt/ReceiptDetailsWidget');
var applicationUtils = require('./../applicationUtils');
var SecureMessageModel = require('./../secure-message/SecureMessageModel');
var financialFeatureConfigModel = require('./../financial/financialFeatureConfigModel');
var screenSizeView = require('./../widget/screen-size/screenSizeView');

var SHOW_ALL_TABS_PROPERTY_NAME = 'showAllTabs';
var TAB_NAME_PROPERTY_NAME = 'tabName';
var BILL_ID_PROPERTY_NAME = 'billId';
var RECEIPT_VECNA_ID_PROPERTY_NAME = 'receiptVecnaId';
var ACCOUNT_EXTERNAL_ID_PROPERTY_NAME = 'accountExternalId';
var APPOINTMENT_EXTERNAL_IDS_PROPERTY_NAME = 'appointmentExternalIds';
var IS_PAYMENT_PROPERTY_NAME = 'isPayment';
var TAB_NAMES = {
  BILLS_DUE: 'due',
  PAID_BILLS: 'paid',
  RECEIPTS: 'receipts',
  ESTIMATES: 'estimates',
  DEFAULT: 'due'
};

var withSidebarClassNameModifier = '';

if (applicationUtils.currentApplication === applicationUtils.APPLICATIONS.ADMIN_TOOL) {
  withSidebarClassNameModifier = ' brec-tab__container--side';
}

/**
 * Perspective to display the lists of bills for a patient.
 *
 * @class BillTabsView
 * @constructor
 */
var BillTabsView = TorsoView.extend({
  tagName: 'section',
  className: 'brec-tab__container' + withSidebarClassNameModifier,
  template: billTabsTemplate,

  behaviors: {
    bill: {
      behavior: SecureMessageDataBehavior,
      returnSingleResult: true,
      renderOnFetch: true,
      alwaysFetch: true,
      id: { property: 'viewState:' + BILL_ID_PROPERTY_NAME}
    }
  },

  events: {
    'click [data-tab-toggle]' : '_toggleTabs'
  },

  prepareFields: {
    tabName: function() {
      return this.get(TAB_NAME_PROPERTY_NAME) || TAB_NAMES.DEFAULT;
    },
    patientVecnaId: '_patientVecnaId',
    TAB_NAMES: TAB_NAMES,
    currentApplication: applicationUtils.currentApplication,
    APPLICATIONS: applicationUtils.APPLICATIONS,
    estimatedCostsEnabled: function() {
      return financialFeatureConfigModel.isEstimatedCostEnabled(financialFeatureConfigModel.CONFIG_LEVEL.NORMAL);
    },
    showTabs: function() {
      var isDefaultScreen = this.get('isDefaultScreen');
      var showTabs = this.get('showTabs');
      var isAdminTool = applicationUtils.currentApplication === applicationUtils.APPLICATIONS.ADMIN_TOOL;
      return !isDefaultScreen || showTabs || isAdminTool;
    }
  },

  /**
   * Detect whether it is in default (not large) screen view
   * @method _updateScreenSize
   * @private
   */
  _updateScreenSize: function() {
    var isDefaultScreen = screenSizeView.currentSizeIsSmaller(screenSizeView.SCREEN_SIZES.MEDIUM_LANDSCAPE);
    this.set('isDefaultScreen', isDefaultScreen);
  },

  /**
   * @method setRouteContext set any context vars passed in by the router.
   * @param routeParameters {Object} hash of parameter names to values from the route.
   *   @param [routeParameters.tabName='due'] {String} the name of the tab that this perspective should display.
   *                                           Should be one of TAB_NAMES or the default list will be displayed.
   *   @param [routeParameters.page=1] {String} the one-based (human readable) page number of the page in the list to render.
   *   @param [routeParameters.billId=null] {Number} the id of the bill details to display.
   *   @param [routeParameters.appointmentExternalIds=null] {String[]} the ids of the appointments to pre-select the copays for.
   *   @param [routeParameters.receiptVecnaId=null] {String} the vecna id of the receipt details to display.
   *   @param [routeParameters.isPayment=false] {Boolean} whether to display the make payment content for this view.
   *   @param [routeParameters.accountExternalId=null] {String} the external id of the account to display.
   */
  setRouteContext: function(routeParameters) {
    var mappingRouteParameters = _.pick(routeParameters, 'tabName', 'billId', 'receiptVecnaId', 'isPayment',
                                        'accountExternalId', 'appointmentExternalIds');
    var updatesToViewState = this._mapRouteParametersToViewState(mappingRouteParameters);
    this.set(updatesToViewState);

    var currentContentWidget = this._getOrCreateCurrentContentWidget();
    if (currentContentWidget && _.isFunction(currentContentWidget.setPage)) {
      currentContentWidget.setPage(routeParameters.page);
    }

    this._fetchWidgetData();
    this.render();
  },

  /**
   * @method initialize
   * @override
   * @param options {Object} the options of this perspective.
   *   @param options.patientVecnaId {String} the patient id of the patient that this perspective is displaying.
   *   @param [options.billId=undefined] {String} the id of the bill to display the details of (empty means display list).
   *   @param [options.appointmentExternalIds=null] {String[]} the ids of the appointments to pre-select the copays for.
   *   @param [options.receiptVecnaId=undefined] {String} the vecnaId of the receipt to display the details of (empty means display list).
   *   @param [options.page=1] {String} the page of the list view to display.
   *   @param [options.isPayment=false] {Boolean} whether to display the make payment content for this view.
   */
  initialize: function(options) {
    var routeContext = _.pick(options, 'page', 'tabName', 'billId', 'appointmentExternalIds', 'accountExternalId', 'receiptVecnaId', 'isPayment');
    this._patientVecnaId = options.patientVecnaId;

    this._billDetailsWidgets = {};
    this._receiptDetailsWidgets = {};
    this._estimateDetailsWidgets = {};
    this._listViews = {};

    this.setRouteContext(routeContext);
    this.listenTo(this.getBehavior('bill'), 'fetched', this._updateTabBasedOnBillStatus);
    this.listenTo(this.viewState, 'change', this.render);

    this.listenTo(financialFeatureConfigModel, 'load-complete', this.render);
    financialFeatureConfigModel.loadOnce();
  },

  /**
   * @method attachTrackedViews
   * @override
   */
  attachTrackedViews: function() {
    if (this._contentWidget != null) {
      this.attachView('content-widget', this._contentWidget);
    }
  },

  /**
   * @method _attached
   * @private
   * @override
   */
  _attached: function() {
    // Only render when screen size changes if the view is attached.
    this.listenTo(screenSizeView, 'change:screenSize', this._updateScreenSize);
    this._updateScreenSize();
  },

  /**
   * @method _detached
   * @private
   * @override
   */
  _detached: function() {
    this.stopListening(screenSizeView);
  },

  /**
   *
   * @param routeParameters set any context vars passed in by the router.
   * @param routeParameters {Object} hash of parameter names to values from the route.
   *   @param [routeParameters.tabName='due'] {String} the name of the tab that this perspective should display.
   *                                           Should be one of TAB_NAMES or the default list will be displayed.
   *   @param [routeParameters.page=1] {String} the one-based (human readable) page number of the page in the list to render.
   *   @param [routeParameters.billId=null] {Number} the id of the bill details to display.
   *   @param [routeParameters.appointmentExternalIds=null] {String[]} the ids of the appointments to pre-select the copays for.
   *   @param [routeParameters.receiptVecnaId=null] {String} the vecna id of the receipt details to display.
   *   @param [routeParameters.isPayment=false] {Boolean} whether to display the make payment content for this view.
   *   @param [routeParameters.accountExternalId=null] {String} the external id of the account to display
   * @return {{}} - updated state to identify which tab to highlight and details to render
   * @private
   */
  _mapRouteParametersToViewState: function (routeParameters) {
    var updatesToViewState = {};
    var billId = parseInt(routeParameters.billId);

    updatesToViewState[SHOW_ALL_TABS_PROPERTY_NAME] = false;
    if (routeParameters.isPayment) {
      updatesToViewState[TAB_NAME_PROPERTY_NAME] = TAB_NAMES.BILLS_DUE;
      updatesToViewState[BILL_ID_PROPERTY_NAME] = billId;
      updatesToViewState[APPOINTMENT_EXTERNAL_IDS_PROPERTY_NAME] = routeParameters.appointmentExternalIds;
      updatesToViewState[ACCOUNT_EXTERNAL_ID_PROPERTY_NAME] = routeParameters.accountExternalId;
      updatesToViewState[RECEIPT_VECNA_ID_PROPERTY_NAME] = null;
      updatesToViewState[IS_PAYMENT_PROPERTY_NAME] = true;
    } else if (routeParameters.billId) {
      // explicitly don't change the tab name so that it can be set when the bill is fetched.
      updatesToViewState[BILL_ID_PROPERTY_NAME] = billId;
      updatesToViewState[RECEIPT_VECNA_ID_PROPERTY_NAME] = null;
      updatesToViewState[ACCOUNT_EXTERNAL_ID_PROPERTY_NAME] = null;
      updatesToViewState[IS_PAYMENT_PROPERTY_NAME] = false;
    } else if (routeParameters.receiptVecnaId) {
      updatesToViewState[TAB_NAME_PROPERTY_NAME] = TAB_NAMES.RECEIPTS;
      updatesToViewState[BILL_ID_PROPERTY_NAME] = null;
      updatesToViewState[RECEIPT_VECNA_ID_PROPERTY_NAME] = routeParameters.receiptVecnaId;
      updatesToViewState[ACCOUNT_EXTERNAL_ID_PROPERTY_NAME] = null;
      updatesToViewState[IS_PAYMENT_PROPERTY_NAME] = false;
    } else if (routeParameters.accountExternalId) {
      updatesToViewState[TAB_NAME_PROPERTY_NAME] = TAB_NAMES.ESTIMATES;
      updatesToViewState[BILL_ID_PROPERTY_NAME] = null;
      updatesToViewState[RECEIPT_VECNA_ID_PROPERTY_NAME] = null;
      updatesToViewState[ACCOUNT_EXTERNAL_ID_PROPERTY_NAME] = routeParameters.accountExternalId;
      updatesToViewState[IS_PAYMENT_PROPERTY_NAME] = false;
    } else {
      updatesToViewState[TAB_NAME_PROPERTY_NAME] = routeParameters.tabName || TAB_NAMES.DEFAULT;
      updatesToViewState[BILL_ID_PROPERTY_NAME] = null;
      updatesToViewState[RECEIPT_VECNA_ID_PROPERTY_NAME] = null;
      updatesToViewState[ACCOUNT_EXTERNAL_ID_PROPERTY_NAME] = null;
      updatesToViewState[IS_PAYMENT_PROPERTY_NAME] = false;
    }
    return updatesToViewState;
  },

  /**
   * The mobile toggler for the tabs
   * @method _toggleTabs
   * @private
  */
  _toggleTabs: function() {
    var showTabs = this.get('showTabs');
    this.set('showTabs', !showTabs);
  },

  /**
   * Retrieves the list associated with the currently selected tab (or the default list if not found).
   * @method _getCurrentContentWidget
   * @returns {Torso.View}
   * @private
   */
  _getOrCreateCurrentContentWidget: function() {
    var tabName = this.get(TAB_NAME_PROPERTY_NAME);
    var isPayment = this.get(IS_PAYMENT_PROPERTY_NAME);
    var billId = this.get(BILL_ID_PROPERTY_NAME);
    var appointmentExternalIds = this.get(APPOINTMENT_EXTERNAL_IDS_PROPERTY_NAME);
    var receiptVecnaId = this.get(RECEIPT_VECNA_ID_PROPERTY_NAME);
    var accountExternalId = this.get(ACCOUNT_EXTERNAL_ID_PROPERTY_NAME);
    var existingListWidget = !isPayment && !billId && !receiptVecnaId && !accountExternalId && this._listViews[tabName];
    var existingBillDetailsWidget = !isPayment && this._billDetailsWidgets[billId];
    var existingReceiptDetailsWidget = !isPayment && this._receiptDetailsWidgets[receiptVecnaId];
    var existingContentWidget = !isPayment && (existingBillDetailsWidget || existingReceiptDetailsWidget || existingListWidget);

    this._contentWidget = null; // get rid of now-stale reference, in case we are rendered from some triggered event

    if (existingContentWidget) {
      this._contentWidget = existingContentWidget;
    } else if (isPayment) {
      this._contentWidget = this._createAndRegisterPaymentWidget(billId, accountExternalId, appointmentExternalIds);
    } else if (billId) {
      this._contentWidget = this._createAndRegisterNewBillDetailsWidget(billId);
    } else if (receiptVecnaId) {
      this._contentWidget = this._createAndRegisterNewReceiptDetailsWidget(receiptVecnaId);
    } else if (accountExternalId) {
      this._contentWidget = this._createAndRegisterNewEstimateDetailsWidget(accountExternalId);
    } else if (tabName === TAB_NAMES.BILLS_DUE) {
      this._contentWidget = this._createAndRegisterNewBillsDueListWidget();
    } else if (tabName === TAB_NAMES.PAID_BILLS) {
      this._contentWidget = this._createAndRegisterNewPaidBillsListWidget();
    } else if (tabName === TAB_NAMES.RECEIPTS) {
      this._contentWidget = this._createAndRegisterNewReceiptMessagesListWidget();
    } else if (tabName === TAB_NAMES.ESTIMATES) {
      this._contentWidget = this._createAndRegisterNewEstimatesListWidget();
    } else {
      this._contentWidget = this._createAndRegisterNewBillsDueListWidget();
    }
    return this._contentWidget;
  },

  /**
   * Creates a new MakePaymentWidget.  Always create a new view so that previous state (payment info) is removed.
   * @method _createAndRegisterPaymentWidget
   * @param billId {String} the bill id of the payment widget to create and register.
 *   @param [appointmentExternalIds=null] {String[]} the ids of the appointments to pre-select the copays for.
   * @returns {MakePaymentWidget}
   * @private
   */
  _createAndRegisterPaymentWidget: function(billId, accountExternalId, appointmentExternalIds) {
    if (this._paymentDetailsWidget) {
      this.unregisterTrackedView(this._paymentDetailsWidget);
      this._paymentDetailsWidget.dispose();
    }
    this._paymentDetailsWidget = new MakePaymentWidget({
      isInPreReg: false,
      patientVecnaId: this._patientVecnaId,
      billId: billId,
      accountExternalId: accountExternalId,
      appointmentExternalIds: appointmentExternalIds
    });
    return this._paymentDetailsWidget;
  },

  /**
   * Creates a new BillDetailsWidget and registers it by bill id.
   * @method _createAndRegisterNewBillDetailsWidget
   * @param billId {String} the id of the bill widget to create and register.
   * @returns {BillDetailsView}
   * @private
   */
  _createAndRegisterNewBillDetailsWidget: function(billId) {
    var billDetailsWidget = new BillDetailsView({
      patientVecnaId: this._patientVecnaId,
      billId: billId
    });
    this._billDetailsWidgets[billId] = billDetailsWidget;
    return billDetailsWidget;
  },

  /**
   * Creates a new ReceiptDetailsWidget and registers it by receipt vecna id.
   * @method _createAndRegisterNewBillDetailsWidget
   * @param receiptVecnaId {String} the vecna id of the receipt widget to create and register.
   * @returns {ReceiptDetailsWidget}
   * @private
   */
  _createAndRegisterNewReceiptDetailsWidget: function(receiptVecnaId) {
    var receiptDetailsWidget = new ReceiptDetailsWidget({
      patientVecnaId: this._patientVecnaId,
      receiptVecnaId: receiptVecnaId
    });
    this._receiptDetailsWidgets[receiptVecnaId] = receiptDetailsWidget;
    return receiptDetailsWidget;
  },

  /**
   * Creates a new ReceiptDetailsWidget and registers it by receipt vecna id.
   * @method _createAndRegisterNewBillDetailsWidget
   * @param accountExternalId {String} the external id of the account to create the widget for.
   * @returns {ReceiptDetailsWidget}
   * @private
   */
  _createAndRegisterNewEstimateDetailsWidget: function(accountExternalId) {
    var estimateDetailsWidget = new EstimateDetailsWidget({
      patientVecnaId: this._patientVecnaId,
      accountExternalId: accountExternalId
    });
    this._estimateDetailsWidgets[accountExternalId] = estimateDetailsWidget;
    return estimateDetailsWidget;
  },

  /**
   * Creates a new BillsDueListWidget and registers it by tab name.
   * @method _createAndRegisterNewBillsDueListWidget
   * @returns {BillsDueListWidget}
   * @private
   */
  _createAndRegisterNewBillsDueListWidget: function() {
    var billsDueListWidget = new BillsDueListWidget({patientVecnaId: this._patientVecnaId});
    this._listViews[TAB_NAMES.BILLS_DUE] = billsDueListWidget;
    return billsDueListWidget;
  },

  /**
   * Creates a new BillsPaidListWidget and registers it by tab name.
   * @method _createAndRegisterNewPaidBillsListWidget
   * @returns {BillsPaidListWidget}
   * @private
   */
  _createAndRegisterNewPaidBillsListWidget: function() {
    var paidBillsListWidget = new BillsPaidListWidget({patientVecnaId: this._patientVecnaId});
    this._listViews[TAB_NAMES.PAID_BILLS] = paidBillsListWidget;
    return paidBillsListWidget;
  },

  /**
   * Creates a new ReceiptMessagesListWidget and registers it by tab name.
   * @method _createAndRegisterNewReceiptMessagesListWidget
   * @returns {ReceiptMessagesListWidget}
   * @private
   */
  _createAndRegisterNewReceiptMessagesListWidget: function() {
    var receiptMessagesListWidget = new ReceiptMessagesListWidget({patientVecnaId: this._patientVecnaId});
    this._listViews[TAB_NAMES.RECEIPTS] = receiptMessagesListWidget;
    return receiptMessagesListWidget;
  },

  /**
   * Creates a new EstimateListWidget and registers it by tab name.
   * @method _createAndRegisterNewEstimatesListWidget
   * @returns {EstimatesListWidget}
   * @private
   */
  _createAndRegisterNewEstimatesListWidget: function() {
    var estimatesListWidget = new EstimatesBrecListView({patientVecnaId: this._patientVecnaId});
    this._listViews[TAB_NAMES.ESTIMATES] = estimatesListWidget;
    return estimatesListWidget;
  },

  /**
   * Forces a refresh of the list by fetching it from the server on the currently displayed list (based on the selected tab).
   * If the widget does not define a fetchContent method then nothing is done here.
   * @method _fetchWidgetData
   * @private
   */
  _fetchWidgetData: function() {
    var currentContentWidget = this._contentWidget;
    if (_.isFunction(currentContentWidget.fetchContent)) {
      currentContentWidget.fetchContent();
    }
  },

  /**
   * Updates the tab to PAID if the bill is paid and DUE if the bill is not PAID.
   * @method _updateTabBasedOnBillStatus
   * @private
   */
  _updateTabBasedOnBillStatus: function() {
    var bill = this.getBehavior('bill').data.getModel();
    if (!bill) {
      return;
    }
    var billStatus = bill.get('billDTO.externalBill.status');
    var isPaidBill = (billStatus === SecureMessageModel.BILL_STATUSES.PAID);
    if (isPaidBill) {
      this.set(TAB_NAME_PROPERTY_NAME, TAB_NAMES.PAID_BILLS);
    } else {
      this.set(TAB_NAME_PROPERTY_NAME, TAB_NAMES.BILLS_DUE);
    }
  }
});

BillTabsView.TAB_NAMES = TAB_NAMES;

module.exports = BillTabsView;
